import { WidgetConfigurationProps } from 'controls/designer/features/widgets/widgetConfig';

import { makeAutoObservable } from 'mobx';
import { apiFetch } from 'framework/api';
import { newGuid } from 'tools/guid';
import { MobxManager } from 'framework/mobx-integration';
import { TimePeriod } from 'controls/react/form/timePeriodSelector';
import { DataSourceManager } from 'controls/designer/features/widgets/datasources/shared/utils/data-source-manager/dataSourceManager';
import { PivotManager } from 'controls/designer/features/widgets/datasources/shared/utils/pivot-manager/pivotManager';
import { clearDatasourceCache } from 'controls/designer/features/widgets/datasources/api';
import { convertDataSelectorTypeToTimeSelectorType } from 'controls/designer/features/widgets/datasources/datasourceStores';
import {
	getTimeColumns,
	applyTimeColumnDefaults,
} from 'controls/designer/features/widgets/datasources/shared/components/time-period-settings-section/utils';

import { DataSourceDataGridWidgetConfig } from '../types';

export class DataSourceDataGridConfigStore {
	manager: DataSourceManager;
	pivotManager: PivotManager<DataSourceDataGridWidgetConfig>;
	requestId = newGuid();
	enableTimeSelector: boolean;
	mobx = new MobxManager();

	constructor(public props: WidgetConfigurationProps<DataSourceDataGridWidgetConfig>) {
		this.enableTimeSelector = !!this.props.config.period;

		makeAutoObservable(this);

		this.manager = new DataSourceManager({
			selection: this.props.config,
			loadDatasourceList: true,
			requestId: this.requestId,
		});

		this.pivotManager = new PivotManager({
			model: this.props.config,
			requestId: this.requestId,
		});

		this.mobx.reaction(
			() => this.enableTimeSelector,
			() => {
				if (this.enableTimeSelector) {
					this.props.config.period ??= { ...this.defaultTimeSelector };
				} else {
					this.props.config.period = undefined;
				}
			},
		);

		this.mobx.reaction(
			() => this.timeColumns,
			(timeColumns) => applyTimeColumnDefaults(timeColumns, this, this.props.config),
		);
	}

	get dataSource() {
		return this.manager.dataSources.data;
	}

	get currentDataSource() {
		return this.manager.selectedDataSource;
	}

	get defaultTimeSelector(): TimePeriod {
		return convertDataSelectorTypeToTimeSelectorType(this.currentDataSource?.dataSelector);
	}

	get timeColumns() {
		return getTimeColumns(this.manager.rulesConfiguration);
	}

	destroy() {
		this.mobx.destroy();
		this.manager.destroy();
		this.pivotManager.destroy();
		void apiFetch(clearDatasourceCache({ accountId: this.props.config.accountId, requestId: this.requestId }));
	}
}
